import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { getProductImageUrl, hasProductImage } from '../utils/imageUtils';
import ProductImage from '../components/common/ProductImage';

const CartPage: React.FC = () => {
    const { isAuthenticated } = useAuth();
    const { items, totalItems, totalAmount, updateQuantity, removeFromCart, clearCart } = useCart();
    const navigate = useNavigate();

    const formatPrice = (price: number) => {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'EUR'
        }).format(price);
    };

    const getItemPrice = (item: any) => {
        return typeof item.product?.price === 'string'
            ? parseFloat(item.product.price)
            : item.product?.price || 0;
    };

    const handleQuantityChange = (productId: number, newQuantity: number) => {
        if (newQuantity <= 0) {
            removeFromCart(productId);
        } else {
            updateQuantity(productId, newQuantity);
        }
    };

    const handleCheckout = () => {
        if (!isAuthenticated) {
            navigate('/login?redirect=/checkout');
        } else {
            navigate('/checkout');
        }
    };

    if (items.length === 0) {
        return (
            <div className="min-h-screen bg-eldai-gray-light">
                <div className="container mx-auto px-4 py-8">
                    <div className="text-center py-16 column-center">
                        <div className="text-6xl mb-4">🛒</div>
                        <h1 className="text-3xl font-bold text-eldai-blue mb-4">
                            Votre panier est vide
                        </h1>
                        <p className="text-eldai-gray mb-8">
                            Découvrez nos produits casher et ajoutez-les à votre panier
                        </p>
                        <Link
                            to="/catalog"
                            className="nav-link bg-eldai-blue text-white p-3 rounded-lg font-semibold hover:bg-eldai-light-blue transition-colors"
                        >
                            Voir le catalogue
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-eldai-gray-light">
            <div className="container mx-auto px-4 py-8">
                <h1 className="text-3xl font-bold text-eldai-blue mb-8">
                    Mon panier ({totalItems} article{totalItems !== 1 ? 's' : ''})
                </h1>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Cart Items */}
                    <div className="lg:col-span-2 space-y-4">
                        {items.map((item) => (
                            <div key={item.id} className="bg-white rounded-lg shadow-md p-6">
                                <div className="flex items-center space-x-4">
                                    {/* Product Image */}
                                    <Link to={`/product/${item.product_id}`} className="flex-shrink-0">
                                        <ProductImage
                                            product={item.product!}
                                            size="large"
                                        />
                                    </Link>

                                    {/* Product Info */}
                                    <div className="flex-1">
                                        <Link
                                            to={`/product/${item.product_id}`}
                                            className="text-lg font-semibold text-eldai-blue hover:text-eldai-light-blue"
                                        >
                                            {item.product?.name}
                                        </Link>
                                        {item.product?.kosher_certification && (
                                            <p className="text-sm text-eldai-gray">
                                                Certifié casher: {item.product.kosher_certification}
                                            </p>
                                        )}
                                        <p className="text-xl font-bold text-eldai-blue mt-2">
                                            {formatPrice(getItemPrice(item))}
                                        </p>
                                    </div>

                                    {/* Quantity Controls */}
                                    <div className="flex items-center space-x-2">
                                        <button
                                            onClick={() => handleQuantityChange(item.product_id, item.quantity - 1)}
                                            className="w-8 h-8 bg-eldai-gray-light rounded-full flex items-center justify-center hover:bg-eldai-gray transition-colors"
                                        >
                                            −
                                        </button>
                                        <span className="w-12 text-center font-semibold">
                                            {item.quantity}
                                        </span>
                                        <button
                                            onClick={() => handleQuantityChange(item.product_id, item.quantity + 1)}
                                            className="w-8 h-8 bg-eldai-gray-light rounded-full flex items-center justify-center hover:bg-eldai-gray transition-colors"
                                        >
                                            +
                                        </button>
                                    </div>

                                    {/* Item Total */}
                                    <div className="text-right">
                                        <p className="text-lg font-bold text-eldai-blue">
                                            {formatPrice(getItemPrice(item) * item.quantity)}
                                        </p>
                                        <button
                                            onClick={() => removeFromCart(item.product_id)}
                                            className="text-red-600 hover:text-red-800 text-sm"
                                        >
                                            Supprimer
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Order Summary */}
                    <div className="bg-white rounded-lg shadow-md p-6 h-fit">
                        <h2 className="text-xl font-bold text-eldai-blue mb-4">
                            Résumé de la commande
                        </h2>

                        <div className="space-y-3 mb-6">
                            <div className="flex justify-between">
                                <span>Sous-total ({totalItems} articles)</span>
                                <span>{formatPrice(totalAmount)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span>Livraison</span>
                                <span className="text-green-600">Gratuite</span>
                            </div>
                            <div className="border-t pt-3">
                                <div className="flex justify-between text-lg font-bold">
                                    <span>Total</span>
                                    <span className="text-eldai-blue">{formatPrice(totalAmount)}</span>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-3">
                            <button
                                onClick={handleCheckout}
                                className="w-full bg-eldai-yellow text-eldai-blue py-3 px-6 rounded-lg font-semibold hover:bg-eldai-blue hover:text-white transition-colors"
                            >
                                {isAuthenticated ? 'Passer commande' : 'Se connecter pour commander'}
                            </button>

                            <Link
                                to="/catalog"
                                className="block w-full text-center border-2 border-eldai-blue text-eldai-blue py-3 px-6 rounded-lg font-semibold hover:bg-eldai-blue hover:text-white transition-colors"
                            >
                                Continuer les achats
                            </Link>

                            <button
                                onClick={clearCart}
                                className="w-full text-red-600 hover:text-red-800 py-2 text-sm"
                            >
                                Vider le panier
                            </button>
                        </div>

                        {/* Payment Info */}
                        <div className="mt-6 p-4 bg-eldai-gray-light rounded-lg">
                            <div className="flex items-center space-x-2 text-sm text-eldai-gray">
                                <span>💳</span>
                                <span>Paiement à la livraison</span>
                            </div>
                            <div className="flex items-center space-x-2 text-sm text-eldai-gray mt-1">
                                <span>🚚</span>
                                <span>Livraison gratuite</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CartPage;
